import { formatContactName } from "@/modules/billing/billingUtils";
import {
  buildInvoicePaymentSchedule,
  computeInvoiceBalanceDue,
  invoicePaymentModeFromRecord,
} from "@/modules/billing/invoicePaymentUtils";
import type { ClientInvoice, ClientInvoiceLineItem } from "@/modules/types";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { InvoiceRemainderScheduleConfig } from "@/modules/billing/invoiceRemainderSchedule";
import type { BillingEmailSection } from "../../../supabase/functions/_shared/billingEmailLayout.ts";
import {
  buildInvoiceSummarySection,
  buildPaymentRequestEmail,
} from "../../../supabase/functions/_shared/billingEmails.ts";
import { workspaceEmailBrand } from "@/lib/workspaceBranding";

export {
  INVOICE_ORGANIZATION_NAME,
  resolveInvoiceOrganizationName,
} from "@/modules/billing/invoiceOrganizationInfo";
import {
  effectiveTemplate,
  messageTemplateByKey,
  renderMessageTemplate,
  type MessageTemplateEdit,
} from "../../../supabase/functions/_shared/messageTemplates.ts";

export const formatInvoiceMoney = (amount: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(Number(amount) || 0);

export const formatInvoiceDueDate = (dueDate: string) => {
  const parsed = new Date(`${dueDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return dueDate;
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/** Opens the public invoice portal directly on Stripe payment entry. */
export const withInvoicePaymentQuery = (url: string) => {
  if (!url) return url;
  if (/[?&]pay=1(?:&|$)/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}pay=1`;
};

/** Share/send URLs always open ticket-style payment entry (?pay=1). */
export const resolveClientInvoiceShareUrl = (
  result: { url: string; short_url?: string | null },
  origin = typeof window !== "undefined" ? window.location.origin : "",
) => {
  const candidate = result.short_url?.trim() || result.url?.trim() || "";
  if (!candidate) return "";
  const resolved = candidate.startsWith("http")
    ? candidate
    : `${origin}${candidate}`;
  return withInvoicePaymentQuery(resolved);
};

/** Values a client invoice fills into its message templates. */
const invoiceTemplateValues = (
  invoice: ClientInvoice | null | undefined,
  organizationName: string | null | undefined,
  contact?: Contact | null,
) => {
  const balanceDue = invoice
    ? computeInvoiceBalanceDue(
        Number(invoice.amount) || 0,
        Number(invoice.amount_paid) || 0,
      )
    : 0;
  return {
    client_first_name: formatContactName(contact)?.split(" ")[0] ?? "",
    company_name: organizationName ?? "",
    invoice_number: invoice?.invoice_number ?? "",
    amount: invoice ? formatInvoiceMoney(balanceDue, invoice.currency) : "",
    due_date: invoice?.due_date ? formatInvoiceDueDate(invoice.due_date) : "",
  };
};

const renderInvoiceTemplate = (
  key: "invoices.invoice.email" | "invoices.invoice.sms",
  field: "subject" | "body",
  edit: MessageTemplateEdit | null | undefined,
  values: Record<string, string>,
) => {
  const def = messageTemplateByKey(key)!;
  const template = effectiveTemplate(def, edit);
  return renderMessageTemplate(def, template[field], values);
};

/** Subject of a direct invoice email: the company's template or Sigma's. */
export const buildDefaultInvoiceEmailSubject = (
  invoice: ClientInvoice,
  organizationName: string,
  edit?: MessageTemplateEdit | null,
) =>
  renderInvoiceTemplate(
    "invoices.invoice.email",
    "subject",
    edit,
    invoiceTemplateValues(invoice, organizationName),
  );

export type InvoiceEmailTemplateContext = {
  invoice: ClientInvoice;
  lineItems: ClientInvoiceLineItem[];
  organizationName: string;
  paymentUrl: string;
  contact?: Contact | null;
};

/** Editable message for a direct invoice email (company template or Sigma's). */
export const buildDefaultInvoiceEmailMessage = (
  contact?: Contact | null,
  edit?: MessageTemplateEdit | null,
  context?: { invoice?: ClientInvoice | null; organizationName?: string },
) =>
  renderInvoiceTemplate(
    "invoices.invoice.email",
    "body",
    edit,
    invoiceTemplateValues(context?.invoice, context?.organizationName, contact),
  );

export const buildInvoiceSmsText = ({
  invoice,
  organizationName,
  paymentUrl,
  contact,
  senderFirstName,
  templateEdit,
}: {
  invoice: ClientInvoice;
  organizationName: string;
  paymentUrl: string;
  contact?: Contact | null;
  senderFirstName?: string | null;
  /** Company's edit of the invoice text template. */
  templateEdit?: MessageTemplateEdit | null;
}) =>
  renderInvoiceTemplate("invoices.invoice.sms", "body", templateEdit, {
    ...invoiceTemplateValues(invoice, organizationName, contact),
    sender_first_name: senderFirstName?.trim().split(/\s+/)[0] ?? "",
    payment_link: paymentUrl,
  });

const formatScheduleLabel = (
  row: { key: string; label: string; timing: string },
  upfrontPercent: number,
) => {
  if (row.key === "deposit" && row.timing === "now") {
    return `Deposit due now (${upfrontPercent}%)`;
  }
  if (row.key === "remainder" || row.key.startsWith("remainder-")) {
    return row.key === "remainder" ? "Remaining balance" : row.label;
  }
  return row.label;
};

/** Line items, totals and (deposit plans) the payment schedule. */
const buildInvoiceEmailSections = ({
  invoice,
  lineItems,
}: Pick<
  InvoiceEmailTemplateContext,
  "invoice" | "lineItems"
>): BillingEmailSection[] => {
  const money = (value: number) => formatInvoiceMoney(value, invoice.currency);
  const total = Number(invoice.amount) || 0;
  const sections: BillingEmailSection[] = [
    buildInvoiceSummarySection({
      lines: lineItems.map((line) => ({
        description: line.description,
        amount: Number(line.line_total) || 0,
      })),
      currency: invoice.currency,
      subtotal: Number(invoice.subtotal) || 0,
      feeAmount: Number(invoice.fee_amount) || 0,
      discountAmount: Number(invoice.discount_amount) || 0,
      total,
    }),
  ];

  if (invoicePaymentModeFromRecord(invoice) === "deposit_auto") {
    const upfrontPercent = Number(invoice.upfront_percent ?? 100);
    const scheduleRows = buildInvoicePaymentSchedule({
      total,
      amountPaid: Number(invoice.amount_paid) || 0,
      upfrontPercent,
      autoChargeRemainder: invoice.auto_charge_remainder === true,
      saveCard: invoice.save_card_for_future_charges === true,
      hasPaymentMethod: Boolean(invoice.stripe_payment_method_id?.trim()),
      dueDate: invoice.due_date,
      issueDate: invoice.issue_date,
      remainderSchedule:
        (invoice.remainder_schedule as InvoiceRemainderScheduleConfig | null) ??
        null,
    }).filter((row) => row.timing !== "paid");
    if (
      scheduleRows.some(
        (row) =>
          row.key === "deposit" ||
          row.key === "remainder" ||
          row.key.startsWith("remainder-"),
      )
    ) {
      sections.push({
        kind: "rows",
        title: "Payment schedule",
        rows: scheduleRows.map((row) => [
          formatScheduleLabel(row, upfrontPercent),
          money(row.amount),
        ]),
      });
    }
  }

  return sections;
};

/**
 * Direct invoice email on the shared billing layout (same card as ticket
 * invoices): amount due, due date, Pay button, message, line items, totals.
 */
export const buildInvoicePaymentEmail = (
  context: InvoiceEmailTemplateContext & { message: string },
) => {
  const { invoice } = context;
  const balanceDue = computeInvoiceBalanceDue(
    Number(invoice.amount) || 0,
    Number(invoice.amount_paid) || 0,
  );
  return buildPaymentRequestEmail({
    brand: workspaceEmailBrand(context.organizationName),
    invoiceNumber: invoice.invoice_number,
    amountFormatted: formatInvoiceMoney(balanceDue, invoice.currency),
    dueDate: invoice.due_date,
    paymentUrl: context.paymentUrl,
    message: context.message.trim() || buildDefaultInvoiceEmailMessage(),
    sections: buildInvoiceEmailSections(context),
  });
};

export const resolveInvoiceEmailRecipientName = ({
  contact,
  company,
}: {
  contact?: Contact | null;
  company?: Company | null;
}) => formatContactName(contact) || company?.name?.trim() || null;
